// JobCostMemberClockInTimer.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import edit2Fill from '@iconify/icons-eva/edit-2-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import saveFill from '@iconify/icons-eva/save-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import alertTriangleFill from '@iconify/icons-eva/alert-triangle-fill';
import { motion, AnimatePresence } from 'framer-motion';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  Checkbox,
  Skeleton,
  IconButton,
  TextField,
  InputAdornment,
  Autocomplete,
  Tooltip,
  CircularProgress,
  Divider,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  TablePagination,
  Alert,
  Avatar
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles'; 
import Scrollbar from 'src/components/Scrollbar';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import axios from 'axios';
import moment from 'moment';
import {
  getLabourCatalogUnitCost,
  getLabourCostServiceCatalogRate,
  resolveInstallLaborServiceUnitCost,
  resolveInstallLaborServiceLineCost,
  normalizeStoredLaborRate,
  findInstallLaborServiceRecord,
  shouldUseInstallLaborCatalogRate,
  isRecipeCostInstallLaborTimelog,
  resolveWorkflowStageCatalogRate
} from 'src/utils/laborTimelogCalc';
import { useSnackbar } from 'notistack';
import ConfirmationPopup from '../official-request/ConfirmationPopup';
import Modal from '../blog/ProjectModel';
import QbLogo from 'src/assets/quickbookLogo-removebg-preview.png';

// ----------------------------------------------------------------------

// Alphanumeric comparison helper function (natural sort)
function alphanumericCompare(str1, str2) {
  const normalize = (str) => {
    if (str === null || str === undefined) return '';
    // Strip leading and trailing quotes before comparison
    let normalized = str.toString().trim();
    // Remove leading and trailing quotes (both single and double)
    normalized = normalized.replace(/^["']+|["']+$/g, '');
    return normalized;
  };

  const a = normalize(str1);
  const b = normalize(str2);

  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;

  const getChunks = (str) => {
    const chunks = [];
    let currentChunk = '';
    let chunkType = null; // 'hash', 'otherSpecial', 'digit', or 'letter'

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const isDigit = /\d/.test(char);
      const isLetter = /[a-zA-Z]/.test(char);
      const isHash = char === '#';
      const isOtherSpecial = !isDigit && !isLetter && !isHash;
      
      // Determine chunk type with priority: hash < otherSpecial < digit < letter
      let currentType;
      if (isHash) {
        currentType = 'hash';
      } else if (isOtherSpecial) {
        currentType = 'otherSpecial';
      } else if (isDigit) {
        currentType = 'digit';
      } else {
        currentType = 'letter';
      }

      if (i === 0) {
        chunkType = currentType;
        currentChunk = char;
      } else if (currentType === chunkType) {
        currentChunk += char;
      } else {
        chunks.push({
          value: chunkType === 'digit' ? parseInt(currentChunk, 10) : currentChunk.toLowerCase(),
          type: chunkType
        });
        currentChunk = char;
        chunkType = currentType;
      }
    }

    if (currentChunk) {
      chunks.push({
        value: chunkType === 'digit' ? parseInt(currentChunk, 10) : currentChunk.toLowerCase(),
        type: chunkType
      });
    }

    return chunks;
  };

  const chunksA = getChunks(a);
  const chunksB = getChunks(b);
  const len = Math.min(chunksA.length, chunksB.length);

  // Type priority: hash (0) < otherSpecial (1) < digit (2) < letter (3)
  const getTypePriority = (type) => {
    if (type === 'hash') return 0;
    if (type === 'otherSpecial') return 1;
    if (type === 'digit') return 2;
    return 3; // letter
  };

  for (let i = 0; i < len; i++) {
    const chunkA = chunksA[i];
    const chunkB = chunksB[i];

    const priorityA = getTypePriority(chunkA.type);
    const priorityB = getTypePriority(chunkB.type);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Same type, compare values
    if (chunkA.type === 'digit') {
      if (chunkA.value < chunkB.value) return -1;
      if (chunkA.value > chunkB.value) return 1;
    } else {
      if (chunkA.value < chunkB.value) return -1;
      if (chunkA.value > chunkB.value) return 1;
    }
  }

  if (chunksA.length < chunksB.length) return -1;
  if (chunksA.length > chunksB.length) return 1;

  return 0;
}

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'date', label: 'Date', align: 'left' },
  { id: 'employee', label: 'Employee/Services', align: 'left' },
  { id: 'expt_qty', label: 'Expt Qty', align: 'center' },
  { id: 'hours', label: 'Hours', align: 'center' },
  { id: 'costRate', label: 'Labor Rate', align: 'left' },
  { id: 'cost', label: 'Cost', align: 'left' },
  { id: 'actions', label: 'Actions', align: 'center' },
];

const getTableHead = (isTM) => {
  if (isTM) {
    return [
      { id: 'employee', label: 'Employee/Services', align: 'left' },
      { id: 'hours', label: 'Hours', align: 'center' },
      { id: 'costRate', label: 'Labor Rate', align: 'left' },
      { id: 'cost', label: 'Cost', align: 'left' },
      // OLD: { id: 'actions', label: 'Actions', align: 'center' },
      { id: 'actions', label: 'Actions', align: 'center' },
    ];
  }
  return TABLE_HEAD;
};

const toSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// OLD: /** T&M: Edit/Delete for member labor rows only; Install Labor service row shows "-". */
// const showTmLaborRowActions = (row) => !row.isServiceRow;

const isTmInstallLaborServiceRow = (row) =>
  Boolean(row?.isServiceRow && !row?.isOtherService);

const rowHasLaborHours = (row) =>
  toSafeNumber(row?.hours) + toSafeNumber(row?.ot) > 0;

/** Parse rate from form; 0 is valid. Empty uses fallback (do not use `x || fallback` — it breaks 0). */
const parseLaborRateInput = (value, fallback) => {
  if (value === '' || value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const roundToTwo = (value) => Math.round((toSafeNumber(value) + Number.EPSILON) * 100) / 100;
const sanitizeNonNegativeNumberInput = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const cleaned = String(value).replace(/[^0-9.]/g, '');
  const normalized = cleaned.replace(/(\..*)\./g, '$1');
  if (normalized === '') return '';
  return Math.max(0, Number(normalized));
};
const preventInvalidNumberKeys = (event) => {
  if (['-', '+', 'e', 'E'].includes(event.key)) {
    event.preventDefault();
  }
};
const NO_TASK_INSTALL_LABOR_VALUE = '__install_labor_no_task__';

// OLD: const resolveServiceRate = (service) => { ... used originalCost only, no isTM / install-labor markup path.
const resolveServiceRate = (service, isTM = false) => {
  if (!service) return 0;
  const isInstallLabor = Number(service.labour_cost_service_id) === 10;
  if (isInstallLabor) {
    const installRate = resolveInstallLaborServiceUnitCost(service, isTM);
    if (installRate > 0) return installRate;
  }

  // Member / job-cost clock-in rate — takes precedence over estimate hourly_rate and unit_cost
  const jobcostRate = Number(service.jobcost_unit_cost);
  if (Number.isFinite(jobcostRate) && jobcostRate > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, jobcostRate);
    if (normalized > 0) return normalized;
  }

  // Request line hourly from estimate when jobcost_unit_cost is still 0
  const hourly = service.hourly_rate ?? service.hourlyRate;
  if (hourly != null && hourly !== '' && Number(hourly) > 0) return Number(hourly);

  const catalogCost = getLabourCatalogUnitCost(service, isTM);
  if (catalogCost > 0) return catalogCost;
  // OLD: const originalCost = service.originalCost ?? service.original_cost;
  // OLD: if (originalCost != null && originalCost !== '' && Number(originalCost) > 0) return Number(originalCost);

  const unitCost = service.unit_cost;
  if (unitCost != null && unitCost !== '' && Number(unitCost) > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, Number(unitCost));
    if (normalized > 0) return normalized;
  }

  const qty = Number(service.jobcost_quantity ?? service.quantity ?? service.qty ?? 0);
  const totalC = Number(service.jobcost_total_cost ?? 0);
  if (qty > 0 && totalC > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, totalC / qty);
    if (normalized > 0) return normalized;
  }

  return Number(service.unit_price || 0);
};

/**
 * $/hr to show on **member** rows tied to a request_services line — matches Job Editing / Other tab
 * (original / unit catalog cost before per-staff hourly on the same row).
 */
const resolveServiceLineCostForMemberDisplay = (service, isTM = false) => {
  if (!service) return 0;
  const catalogCost = getLabourCatalogUnitCost(service, isTM);
  if (catalogCost > 0) return catalogCost;
  // OLD: const oc = Number(service.originalCost ?? service.original_cost);
  // OLD: if (Number.isFinite(oc) && oc > 0) return oc;
  const uc = Number(service.unit_cost);
  if (Number.isFinite(uc) && uc > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, uc);
    if (normalized > 0) return normalized;
  }
  const jc = Number(service.jobcost_unit_cost);
  if (Number.isFinite(jc) && jc > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, jc);
    if (normalized > 0) return normalized;
  }
  const qty = Number(service.jobcost_quantity ?? service.quantity ?? service.qty ?? 0);
  const totalC = Number(service.jobcost_total_cost ?? 0);
  if (qty > 0 && totalC > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, totalC / qty);
    if (normalized > 0) return normalized;
  }
  const hourly = service.hourly_rate ?? service.hourlyRate;
  if (hourly != null && hourly !== '' && Number(hourly) > 0) return Number(hourly);
  return Number(service.unit_price || 0);
};

/** Match timelog / task row to a `services` request_services object (id, COP id, or task name). */
const findRequestServiceForLaborUser = (user, servicesList) => {
  const list = servicesList || [];
  const idCandidates = [
    user.service_id,
    user.rs_id,
    user.customer_order_product_id
  ]
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);

  for (const id of idCandidates) {
    const byRsId = list.find((s) => Number(s.id) === id);
    if (byRsId) return byRsId;
    const byCop = list.find((s) => Number(s.customer_order_product_id) === id);
    if (byCop) return byCop;
  }

  const taskName = (user.task_name || '').toString().trim().toLowerCase();
  if (!taskName) return null;
  const exact = list.find((s) => {
    const lab = (s.service_label || s.name || s.service_name || '')
      .toString()
      .trim()
      .toLowerCase();
    return lab && lab === taskName;
  });
  if (exact) return exact;
  return (
    list.find((s) => {
      const lab = (s.service_label || s.name || s.service_name || '')
        .toString()
        .trim()
        .toLowerCase();
      if (!lab) return false;
      return taskName.includes(lab) || lab.includes(taskName);
    }) || null
  );
};

/** Service-row edit form: prefer saved jobcost_unit_cost, then estimate/catalog fallbacks. */
const resolveServiceRateForEdit = (service, isTM = false) => {
  if (!service) return 0;
  const isInstallLabor = Number(service.labour_cost_service_id) === 10;
  if (isInstallLabor) {
    const installRate = resolveInstallLaborServiceUnitCost(service, isTM);
    if (installRate > 0) return installRate;
  }

  const jobcostRate = Number(service.jobcost_unit_cost);
  if (Number.isFinite(jobcostRate) && jobcostRate > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, jobcostRate);
    if (normalized > 0) return normalized;
  }

  const hourly = service.hourly_rate ?? service.hourlyRate;
  if (hourly != null && hourly !== '' && Number(hourly) > 0) return Number(hourly);

  const catalogCost = getLabourCatalogUnitCost(service, isTM);
  if (catalogCost > 0) return catalogCost;
  // OLD: const fromOriginal = service.originalCost ?? service.original_cost;
  // OLD: if (fromOriginal != null && fromOriginal !== '' && Number(fromOriginal) > 0) return Number(fromOriginal);

  const unitCost = service.unit_cost;
  if (unitCost != null && unitCost !== '' && Number(unitCost) > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, Number(unitCost));
    if (normalized > 0) return normalized;
  }

  const qty = Number(service.jobcost_quantity ?? service.quantity ?? service.qty ?? 0);
  const totalC = Number(service.jobcost_total_cost ?? 0);
  if (qty > 0 && totalC > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, totalC / qty);
    if (normalized > 0) return normalized;
  }

  return Number(service.unit_price || 0);
};

const resolveServiceBillRate = (service) => {
  if (!service) return 0;
  const billUnit = service.bill_unit_price ?? service.jobcost_bill_rate;
  if (billUnit != null && billUnit !== '' && Number(billUnit) > 0) return Number(billUnit);
  
  const billRate = service.bill_rate ?? service.jobcost_total_bill;
  if (billRate != null && billRate !== '' && Number(billRate) > 0) return Number(billRate);
  
  return Number(service.unit_bill || 0);
};

const resolveCatalogNameForLaborRow = (user, isInstaller, isDriverMember) => {
  if (!isInstaller && !isDriverMember) return '';
  return (user?.installation_name || user?.product_name || user?.catalog_name || '').toString().trim();
};

const getDisplayEmployeeName = (taskName, staffName, isDriver, isTM = false, catalogName = '') => {
  const safeTask = (taskName || '').toString().trim();
  const safeStaff = (staffName || '').toString().trim();
  const safeCatalog = (catalogName || '').toString().trim();

  if (isDriver || !safeTask) {
    const catalogSuffix = safeCatalog ? ` (${safeCatalog})` : '';
    return `${safeStaff} - Install Labor${catalogSuffix}`;
  }

  const normalizedStaff = safeStaff.toLowerCase();
  const normalizedTask = safeTask.toLowerCase();
  if (normalizedStaff.startsWith(`${normalizedTask} -`) || normalizedStaff === normalizedTask) {
    return safeStaff;
  }
  return `${safeStaff} - ${safeTask}`;
};

const getEmployeeTooltip = (staffName, taskName, catalogName, subtaskName, isInstallLaborRow) => {
  const safeStaff = (staffName || '').toString().trim();
  const safeTask = (taskName || '').toString().trim();
  const safeCatalog = (catalogName || '').toString().trim();
  const safeSubtask = (subtaskName || '').toString().trim();

  if (isInstallLaborRow) {
    const lines = [`${safeStaff} - Install Labor`];
    if (safeCatalog) lines.push(`Catalog: ${safeCatalog}`);
    if (safeSubtask) lines.push(`Sub Task: ${safeSubtask}`);
    return lines.join('\n');
  }

  const base = safeTask ? `${safeStaff} - ${safeTask}` : safeStaff;
  if (safeSubtask) return `${base}\nSub Task: ${safeSubtask}`;
  return base;
};

/** Merge org team list (get-project-by-id teamMembers) with project roster for bill_rate / overrides. */
const mergeTeamMembersForLabor = (teamMembers, rosterMembers) =>
  (teamMembers || []).map((tm) => {
    const pm = (rosterMembers || []).find((p) => Number(p.staff_id) === Number(tm.id));
    const uid = tm.userid ?? tm.user_id ?? pm?.userid ?? pm?.userId;
    return {
      id: tm.id,
      staff_id: tm.id,
      name: tm.name,
      userid: uid,
      userId: uid,
      user_id: uid,
      hourly_rate: pm?.hourly_rate ?? tm.hourly_rate ?? 0,
      bill_rate: pm?.bill_rate ?? 0
    };
  });

async function fetchProjectByIdForLaborDialog(projectId, userId, userType) {
  return axios.post(
    `${REST_API_END_POINT}project/get-project-by-id`,
    {
      id: { value: projectId },
      userId,
      userType,
      hasPermission: true
    },
    { headers: { 'Content-Type': 'application/json' } }
  );
}

async function loadLaborAddDialogData(projectId, userId, userType) {
  const res = await fetchProjectByIdForLaborDialog(projectId, userId, userType);
  if (res.data?.status !== 1) {
    return { members: [], tasks: [] };
  }
  const roster = res.data.projectMembers || [];
  return {
    members: mergeTeamMembersForLabor(res.data.teamMembers || [], roster),
    tasks: res.data.projectTask || []
  };
}

/** Backend returns one row per subtask slot; the same timelog is merged onto each → duplicate lines. Keep one row per timelog. */
function dedupeLaborRowsByTimelog(mappedRows) {
  const seenTimelogIds = new Set();
  const out = [];
  for (const row of mappedRows) {
    const tid = row.originalData?.timelog_id;
    if (tid != null && tid !== '') {
      const key = String(tid);
      if (seenTimelogIds.has(key)) continue;
      seenTimelogIds.add(key);
    }
    out.push(row);
  }
  return out;
}

/** API can return duplicate roster + installer rows for the same member; keep the row with the most hours. */
function mergeLaborRowsPreferringHours(mappedRows) {
  const byStaffKey = new Map();
  const passthrough = [];

  for (const row of mappedRows) {
    const staffUserId = row.originalData?.staff_user_id;
    if (staffUserId == null || staffUserId === '') {
      passthrough.push(row);
      continue;
    }
    const key = [
      staffUserId,
      row.originalData?.installation_id ?? '',
      row.originalData?.task_id ?? '',
      row.originalData?.subtask_id ?? ''
    ].join('|');
    const rowHours = (Number(row.hours) || 0) + (Number(row.ot) || 0);
    const prev = byStaffKey.get(key);
    if (!prev) {
      byStaffKey.set(key, row);
      continue;
    }
    const prevHours = (Number(prev.hours) || 0) + (Number(prev.ot) || 0);
    if (rowHours > prevHours) byStaffKey.set(key, row);
  }

  return [...passthrough, ...byStaffKey.values()];
}

/** Hours from API totals, or live elapsed time when a clock-in is still open. */
function resolveMemberHoursFromTimelog(user) {
  const totalHours = Number(user?.total_hours) || 0;
  const totalMinutes = Number(user?.total_minutes) || 0;
  const otHours = Number(user?.total_ot_hours || user?.ot_hours || 0) || 0;
  const apiDecimalHours = Number(user?.hours) || 0;
  const computedHoursFromTotals = roundToTwo(totalHours + totalMinutes / 60);
  let decimalTotalHours =
    computedHoursFromTotals > 0 ? computedHoursFromTotals : roundToTwo(apiDecimalHours);

  if (decimalTotalHours <= 0) {
    const start = Number(user?.start_time);
    if (Number.isFinite(start) && start > 0) {
      const end =
        user?.end_time != null && user?.end_time !== ''
          ? Number(user.end_time)
          : moment().unix();
      decimalTotalHours = roundToTwo(
        moment.duration(moment.unix(end).diff(moment.unix(start))).asHours()
      );
    }
  }

  const regularHours = roundToTwo(Math.max(0, decimalTotalHours - otHours));
  return { decimalTotalHours, regularHours, otHours: roundToTwo(otHours) };
}

function calculateMargin(total_bill, total_cost) {
  const safeTotalBill = toSafeNumber(total_bill);
  const safeTotalCost = toSafeNumber(total_cost);

  if (!safeTotalBill || safeTotalBill === 0) {
    if (safeTotalCost > 0) return -100;
    return 0;
  }

  return roundToTwo(((safeTotalBill - safeTotalCost) / safeTotalCost) * 100);
}

export default function JobCostMemberClockInTimer({
  userId,
  userType,
  projectData,
  fetchData,
  fetchJobCostLogs,
  isMirrored,
  targetFacilityId,
  projectMembers,
  projectTasks,
  requestId: requestIdProp,
  projectId: projectIdProp,
  onRowsCountChange,
  refreshTrigger,
  onRefreshTrigger,
  onSummaryChange,
  staffTimelogsGet,
  setStaffTimelogsGet,
  services = [],
  isTM = false,
  onServicesRefresh
}) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [rawLogs, setRawLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUid, setEditingUid] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [subtaskOptions, setSubtaskOptions] = useState([]);
  const [laborDialogMembers, setLaborDialogMembers] = useState([]);
  const [laborDialogTasks, setLaborDialogTasks] = useState([]);
  const [loadingLaborDialogData, setLoadingLaborDialogData] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [selectedTaskFilter, setSelectedTaskFilter] = useState(null);
  /** Facility Install Labor catalog when request has no labour_cost_service_id 10 line */
  const [installLaborCatalogStub, setInstallLaborCatalogStub] = useState(null);

  const [editServiceConfirmation, setEditServiceConfirmation] = useState({
    open: false,
    row: null
  });
  const [isSavingService, setIsSavingService] = useState(false);

  const projectId = projectIdProp ?? projectData?.id;
  // Prefer project.request_id over localStorage-derived props ("undefined" / stale SO id breaks estimate Job Log).
  const coerceRequestId = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const resolvedRequestId =
    coerceRequestId(projectData?.request_id) ||
    coerceRequestId(projectData?.requestId) ||
    coerceRequestId(requestIdProp);
  const serviceRatesFingerprint = useMemo(
    () =>
      (services || [])
        .map(
          (s) =>
            // OLD fingerprint: `${Number(s.id)}:${Number(s.jobcost_unit_cost) || 0}:${Number(s.originalCost) || 0}:${Number(s.original_cost) || 0}:${Number(s.unit_cost) || 0}:${Number(s.labour_cost_service_id) || ''}`
            `${Number(s.id)}:${Number(s.jobcost_unit_cost) || 0}:${Number(s.originalCost) || 0}:${Number(s.labour_service_total_cost) || 0}:${Number(s.original_cost) || 0}:${Number(s.unit_cost) || 0}:${Number(s.labour_cost_service_id) || ''}`
        )
        .sort()
        .join('|'),
    [services]
  );

  // OLD: install labor rate only from services.find(labour_cost_service_id === 10) on the request
  const installLaborServiceRecord = useMemo(
    () => findInstallLaborServiceRecord(services, null) || installLaborCatalogStub,
    [services, installLaborCatalogStub]
  );

  // OLD: no facility catalog fetch when request has no Install Labor line — member rows kept staff hourly_rate
  useEffect(() => {
    if ((services || []).some((s) => Number(s.labour_cost_service_id) === 10)) {
      setInstallLaborCatalogStub(null);
      return undefined;
    }
    let cancelled = false;
    axios
      .post(`${REST_API_END_POINT}request/get-labour-cost-services`)
      .then((res) => {
        if (cancelled) return;
        const lcs = (res?.data?.labourCostServices || []).find((s) => Number(s.id) === 10);
        if (lcs) {
          setInstallLaborCatalogStub({
            labour_cost_service_id: 10,
            originalCost: lcs.cost,
            original_cost: lcs.cost,
            cost: lcs.cost,
            labour_service_total_cost: lcs.total_cost,
            service_original_cost: lcs.total_cost,
            unit_cost: lcs.cost,
            name: lcs.name || 'INSTALL LABOR'
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [services]);

  const getAmountBreakdown = (regularHours, otHours, rate) => {
    const reg = Number(regularHours) || 0;
    const ot = Number(otHours) || 0;
    const r = Number(rate) || 0;
    const regularAmount = reg * r;
    const otAmount = ot * r * 1.5;
    const total = regularAmount + otAmount;
    const eq = reg + (ot * 1.5);
    return {
      regularAmount,
      otAmount,
      total,
      equivalentHours: eq,
      text: `Hours: ${reg.toFixed(2)} x $${r.toFixed(2)} = $${regularAmount.toFixed(2)} | Total: $${total.toFixed(2)}`, // Commented out OT: ${ot.toFixed(2)} x $${r.toFixed(2)} x 1.5 = $${otAmount.toFixed(2)}
      hoursText: `${reg.toFixed(2)} Hours` // Commented out Reg + (${ot.toFixed(2)} OT x 1.5) = ${eq.toFixed(2)} Equivalent Hours
    };
  };

  // OLD: const fetchUsersByTask = async () => {
  const fetchUsersByTask = async (silent = false) => {
    try {
      const isTMProject =
        Number(projectData?.project_category) === 12 ||
        projectData?.job_category_name === 'Time & Material';
      let endpoint = isTMProject 
        ? `${REST_API_END_POINT}project/get-all-users-by-tasks`
        : `${REST_API_END_POINT}project/get-all-users-by-tasks`;
      // OLD: setLoading(true);
      if (!silent) setLoading(true);
      
      const res = await axios.post(`${endpoint}`, {
        project_id: projectId,
        userType,
        userId,
        isMirrored: isMirrored,
        targetFacilityId: targetFacilityId,
      });

      if (res.data.status === 1 && res.data.data) {
        const installLaborSvc =
          findInstallLaborServiceRecord(services, null) || installLaborCatalogStub;
        const installLaborUnitCost = installLaborSvc
          ? resolveInstallLaborServiceUnitCost(installLaborSvc, isTMProject)
          : 0;
        const installLaborBillRate = installLaborSvc ? resolveServiceBillRate(installLaborSvc) : 0;

        const mappedRows = res.data.data.map((user) => {
          // OLD: inline hours from API totals only (no start_time/end_time live fallback)
          // const totalHours = Number(user.total_hours) || 0;
          // const totalMinutes = Number(user.total_minutes) || 0;
          // const otHours = Number(user.total_ot_hours || user.ot_hours || 0) || 0;
          // const apiDecimalHours = Number(user.hours) || 0;
          // const computedHoursFromTotals = roundToTwo(totalHours + (totalMinutes / 60));
          // const decimalTotalHours = computedHoursFromTotals > 0 ? computedHoursFromTotals : roundToTwo(apiDecimalHours);
          // const regularHours = roundToTwo(Math.max(0, decimalTotalHours - otHours));
          const { decimalTotalHours, regularHours, otHours } = resolveMemberHoursFromTimelog(user);
          const costRate = user.hourly_rate || 0;
          const billRate = user.bill_rate || 0;
          
          const isInstaller = user.installation === true || user.installation === 1 || user.is_installer === 1;
          const isDriverMember = !user.task_name || String(user.task_name).toLowerCase().includes('driver') || String(user.role_name).toLowerCase().includes('driver') || user.is_driver === 1 || user.is_driver === true;

          const linkedService = findRequestServiceForLaborUser(user, services);
          const useInstallLaborCatalog = shouldUseInstallLaborCatalogRate(user);
          const workflowStageRate = resolveWorkflowStageCatalogRate(user, isTMProject);

          // OLD: Labor Rate always used member hourly_rate / bill_rate unless installer/driver + install-labor fallback below.
          // let finalCostRate = costRate;
          // let finalBillRate = billRate;
          // let finalCostRate = costRate;
          let finalCostRate = 0;
          let finalBillRate = billRate;

          // Subtask tied to onboard_workflow_stages → labour_cost_services (T&M: total_cost, cost card: cost).
          // OLD: member rows always used staff hourly_rate when no install-labor fallback matched
          // Evaluate catalog rate first so we can use it as a fallback if needed
          let catalogRate = 0;
          let catalogBillRate = 0;

          if (workflowStageRate > 0) {
            catalogRate = workflowStageRate;
          } else if (installLaborUnitCost > 0 && useInstallLaborCatalog) {
            catalogRate = installLaborUnitCost;
            if (isTMProject && installLaborBillRate > 0) catalogBillRate = installLaborBillRate;
          } else if (linkedService) {
            const svcCost = resolveServiceLineCostForMemberDisplay(linkedService, isTMProject);
            const svcBill = resolveServiceBillRate(linkedService);
            if (svcCost > 0) {
              catalogRate = svcCost;
              if (isTMProject && svcBill > 0) catalogBillRate = svcBill;
            }
          }

          // T&M only: manual-added task/subtask rows often have no request_services link.
          // In that case, show Install Labor catalog rate instead of member hourly rate.
          const isManualAddedRow =
            Number(user.is_manually_added) === 1 ||
            Number(user.manually_added) === 1 ||
            Number(user.task_is_manually_added) === 1 ||
            Number(user.project_task_is_manually_added) === 1;
          if (isTMProject && isManualAddedRow && installLaborUnitCost > 0) {
            catalogRate = installLaborUnitCost;
            if (installLaborBillRate > 0) catalogBillRate = installLaborBillRate;
          }

          if (user.timelog_id) {
            // If a timelog exists, respect its historically saved rate.
            // BUT if it was saved as 0 (e.g. clocked in before a service was assigned), 
            // allow the new Service Cost to automatically replace the 0.
            if (costRate === 0 && catalogRate > 0) {
              finalCostRate = catalogRate;
              finalBillRate = catalogBillRate || billRate;
            } else {
              finalCostRate = costRate;
              finalBillRate = billRate;
            }
          } else {
            // For un-clocked-in members: use the catalog rate directly.
            finalCostRate = catalogRate;
            finalBillRate = catalogBillRate || billRate;
          }

          // OLD: install-labor service rate applied only when member hourly was 0.
          // if ((isInstaller || isDriverMember) && finalCostRate === 0) {
          //   const installLaborService = (services || []).find((s) => Number(s.labour_cost_service_id) === 10);
          //   if (installLaborService) {
          //     const fallbackCost = resolveServiceRate(installLaborService);
          //     const fallbackBill = resolveServiceBillRate(installLaborService);
          //     if (fallbackCost > 0) finalCostRate = fallbackCost;
          //     if (isTMProject && fallbackBill > 0) finalBillRate = fallbackBill;
          //   }
          // }
          // OLD: install-labor service rate applied only when member hourly was 0 / only if linked service had no cost.
          // if (isInstaller || isDriverMember) {
          //   const linkedSvcCost = linkedService ? resolveServiceLineCostForMemberDisplay(linkedService, isTMProject) : 0;
          //   if (!(linkedSvcCost > 0)) {
          //     const installLaborService = (services || []).find((s) => Number(s.labour_cost_service_id) === 10);
          //     if (installLaborService) {
          //       const fallbackCost = resolveServiceLineCostForMemberDisplay(installLaborService, isTMProject);
          //       const fallbackBill = resolveServiceBillRate(installLaborService);
          //       if (fallbackCost > 0) finalCostRate = fallbackCost;
          //       if (isTMProject && fallbackBill > 0) finalBillRate = fallbackBill;
          //     }
          //   }
          // }

          const actualCost = (isInstaller && !isTMProject) ? (decimalTotalHours * finalCostRate) : (regularHours * finalCostRate) + (otHours * finalCostRate * 1.5);
          const actualBill = (isInstaller && !isTMProject) ? (decimalTotalHours * finalBillRate) : (regularHours * finalBillRate) + (otHours * finalBillRate * 1.5);

          const isInstallLaborRow = isDriverMember || isInstaller || !user.task_name;
          const catalogName = resolveCatalogNameForLaborRow(user, isInstaller, isDriverMember);
          const subtaskName = (user.subtask_name || '').toString().trim();

          return {
            ...user,
            uid: user.timelog_id || `${user.staff_user_id}-${user.task_id || '0'}-${user.subtask_id || '0'}-${user.installation_id || '0'}`,
            date: user.jobcost_timestamp ? moment.unix(user.jobcost_timestamp).format('DD/MM/YYYY') : (user.date ? moment(user.date).format('DD/MM/YYYY') : (user.created_at ? moment(user.created_at).format('DD/MM/YYYY') : "-")),
            employee: getDisplayEmployeeName(user.task_name, user.staff_name, isInstallLaborRow, isTMProject, catalogName),
            employeeTooltip: getEmployeeTooltip(user.staff_name, user.task_name, catalogName, subtaskName, isInstallLaborRow),
            isDriver: isDriverMember,
            isInstaller,
            hours: (isInstaller && !isTMProject) ? decimalTotalHours : regularHours,
            ot: (isInstaller && !isTMProject) ? 0 : otHours,
            costRate: finalCostRate,
            billRate: (isTMProject && (isInstaller || isDriverMember)) ? 0 : finalBillRate,
            cost: actualCost,
            bill: (isTMProject && (isInstaller || isDriverMember)) ? 0 : actualBill,
            _actualCostForAggregation: actualCost,
            _actualBillForAggregation: actualBill,
            _actualCostRate: finalCostRate,
            _actualBillRate: finalBillRate,
            _actualHours: (isInstaller && !isTMProject) ? decimalTotalHours : regularHours,
            billable: user.billable !== undefined
              ? Boolean(Number(user.billable))
              : user.is_billable !== undefined
                ? Boolean(Number(user.is_billable))
                : true,
            warning: user.is_rate_overridden == 1 || user.is_overridden == 1,
            originalData: user
          };
        });
        // OLD: const dedupedRows = dedupeLaborRowsByTimelog(mappedRows);
        const dedupedRows = mergeLaborRowsPreferringHours(dedupeLaborRowsByTimelog(mappedRows));
        setRawLogs(dedupedRows);
        if (setStaffTimelogsGet) {
          const freshOriginalLogs = dedupedRows.map((r) => r.originalData).filter(Boolean);
          setStaffTimelogsGet(freshOriginalLogs);
        }
        if (onRowsCountChange) {
          // Total count is handled by the useEffect on [rows]
        }
      }
    } catch (error) {
      console.error('Error fetching users by task:', error);
    } finally {
      // OLD: setLoading(false);
      if (!silent) setLoading(false);
    }
  };

  /** After labor add/edit/delete: refresh local rows first, then parent job-cost data + refreshTrigger. */
  const syncLaborDataAfterMutation = async () => {
    await fetchUsersByTask();
    if (onServicesRefresh) {
      onServicesRefresh();
    } else {
      if (fetchJobCostLogs) fetchJobCostLogs();
      if (fetchData) fetchData(true);
    }
    // Non-T&M CompareJobCost / production labor list rely on staffTimelogsGet + services staying in sync.
    if (!isTM && onRefreshTrigger) {
      onRefreshTrigger();
    }
  };

  const rows = useMemo(() => {
    const isTMProject =
      Number(projectData?.project_category) === 12 ||
      projectData?.job_category_name === 'Time & Material';
    
    // 1. Prepare base logs
    const baseLogs = rawLogs.map(r => ({
      ...r,
      exptQty: '-',
      // Helper to identify if this log is "Install Labor" related
      // OLD: isInstallLaborRelated: (r.isInstaller || r.isDriver || Number(r.service_id || r.rs_id || r.customer_order_product_id) === 10)
      // Backup before Install Labor request-service classification:
      // isInstallLaborRelated: shouldUseInstallLaborCatalogRate(r) || isRecipeCostInstallLaborTimelog(r)
      isInstallLaborRelated: shouldUseInstallLaborCatalogRate(r) || isRecipeCostInstallLaborTimelog(r, services)
    }));

    const otherServices = (services || []).filter(s => Number(s.labour_cost_service_id) !== 10);
    const installLaborService = installLaborServiceRecord;

    // 2. Function to create a service row (consolidated or fixed)
    const createServiceRow = (s, isInstallLabor = false) => {
      const sId = Number(s.id);
      const isTMConsolidation = isTM && isTMProject;
      
      let relevantLogs = [];
      if (isInstallLabor) {
        relevantLogs = baseLogs.filter(r => r.isInstallLaborRelated);
      } else {
        relevantLogs = baseLogs.filter(r => Number(r.service_id || r.rs_id || r.customer_order_product_id) === sId);
      }

      const totalHoursFromLogs = relevantLogs.reduce((sum, r) => sum + (Number(r._actualHours) || 0), 0);
      const totalCostFromLogs = relevantLogs.reduce((sum, r) => sum + (Number(r._actualCostForAggregation) || 0), 0);
      const totalBillFromLogs = relevantLogs.reduce((sum, r) => sum + (Number(r._actualBillForAggregation) || 0), 0);
      const anyMemberHasRate = relevantLogs.some(r => Number(r._actualCostRate) > 0);

      let sCostRate = resolveServiceRate(s, isTMProject);
      const sBillRate = resolveServiceBillRate(s);

      // Consolidate rate from logs only when request_services has no saved jobcost_unit_cost.
      // If DB already has jobcost_unit_cost, that is the authoritative displayed Labor Rate.
      const hasSavedJobcostUnitRate = Number(s.jobcost_unit_cost) > 0;
      if (isTMConsolidation && !hasSavedJobcostUnitRate && anyMemberHasRate && totalHoursFromLogs > 0) {
        sCostRate = totalCostFromLogs / totalHoursFromLogs;
      }

      const sHours = Number(s.jobcost_quantity || s.quantity || 0);
      // OLD: const displayHours = roundToTwo(sHours > 0 ? sHours : (isTMConsolidation ? totalHoursFromLogs : 0));
      // OLD: let finalCost = sHours > 0 ? (sHours * sCostRate) : (isTMConsolidation ? totalCostFromLogs : 0);
      // OLD: let finalBill = sHours > 0 ? (sHours * sBillRate) : (isTMConsolidation ? totalBillFromLogs : 0);
      // Install Labor (incl. non-T&M): roll up member clock-in hours when jobcost qty not saved yet.
      const rollupHoursFromClockIns = isTMConsolidation || isInstallLabor;
      // Backup before Install Labor member-hour rollup (stale jobcost_quantity e.g. 12.99 vs member sum 15.30):
      // const displayHours = roundToTwo(
      //   sHours > 0 ? sHours : (rollupHoursFromClockIns ? totalHoursFromLogs : 0)
      // );
      // let finalCost = sHours > 0 ? (sHours * sCostRate) : (rollupHoursFromClockIns ? totalCostFromLogs : 0);
      // let finalBill = sHours > 0 ? (sHours * sBillRate) : (rollupHoursFromClockIns ? totalBillFromLogs : 0);
      // Install Labor service row must equal sum of visible member hours when clock-ins exist.
      const useMemberHourRollup = isInstallLabor && totalHoursFromLogs > 0;
      const displayHours = roundToTwo(
        useMemberHourRollup
          ? totalHoursFromLogs
          : (sHours > 0 ? sHours : (rollupHoursFromClockIns ? totalHoursFromLogs : 0))
      );

      let finalCost = useMemberHourRollup
        ? roundToTwo(totalHoursFromLogs * sCostRate)
        : (sHours > 0 ? (sHours * sCostRate) : (rollupHoursFromClockIns ? totalCostFromLogs : 0));
      let finalBill = useMemberHourRollup
        ? roundToTwo(totalHoursFromLogs * sBillRate)
        : (sHours > 0 ? (sHours * sBillRate) : (rollupHoursFromClockIns ? totalBillFromLogs : 0));

      if (isTMConsolidation && !sHours && !anyMemberHasRate && sCostRate > 0) {
        finalCost = totalHoursFromLogs * sCostRate;
        finalBill = totalHoursFromLogs * sBillRate;
      }

      const sSavedCost = Number(s.jobcost_total_cost || 0);
      const sSavedBill = Number(s.jobcost_total_bill || 0);

      const jcUnit = Number(s.jobcost_unit_cost);
      const costFromStoredJc =
        Number.isFinite(jcUnit) && jcUnit > 0 && displayHours > 0
          ? roundToTwo(displayHours * jcUnit)
          : null;
      // Backup before member-hour rollup cost fix (stale jobcost_total_cost e.g. $1623.75 with hours 15.30):
      // let lineCost = sSavedCost > 0 ? sSavedCost : finalCost;
      // if (
      //   costFromStoredJc != null &&
      //   sSavedCost > 0 &&
      //   Math.abs(sSavedCost - costFromStoredJc) > 0.05
      // ) {
      //   lineCost = costFromStoredJc;
      // }
      // if (isTMConsolidation && isInstallLabor) {
      //   lineCost = resolveInstallLaborServiceLineCost(s, true, displayHours);
      // }
      // When Install Labor hours are rolled up from members, cost must be hours × rate.
      let lineCost = useMemberHourRollup
        ? finalCost
        : (sSavedCost > 0 ? sSavedCost : finalCost);
      if (
        !useMemberHourRollup &&
        costFromStoredJc != null &&
        sSavedCost > 0 &&
        Math.abs(sSavedCost - costFromStoredJc) > 0.05
      ) {
        lineCost = costFromStoredJc;
      }

      // T&M: legacy saves used cost (no markup) × hours in jobcost_total_cost — show markup total in UI
      if (isTMConsolidation && isInstallLabor) {
        lineCost = useMemberHourRollup
          ? finalCost
          : resolveInstallLaborServiceLineCost(s, true, displayHours);
      }

      // OLD: only corrected when saved total matched markupRate × hours (markupRate from labour_service_total_cost field was often wrong).
      // if (!isTMProject && !isInstallLabor && sSavedCost > 0 && displayHours > 0) { ... }

      // Non-T&M (cost card): Cost = hours × Labor Rate (rate already without markup via resolveServiceRate)
      // OLD: let lineCost = sSavedCost > 0 ? sSavedCost : finalCost; (showed markup totals e.g. $95, $380)
      if (!isTMProject && displayHours > 0 && sCostRate > 0) {
        lineCost = roundToTwo(displayHours * sCostRate);
      }

      return {
        uid: isInstallLabor ? `service-10` : `other-service-${s.id}`,
        serviceId: s.id,
        date: s.jobcost_timestamp ? moment.unix(s.jobcost_timestamp).format('DD/MM/YYYY') : (isInstallLabor ? '-' : (s.date || s.created_at || "-")),
        employee: s.service_label || s.name || (isInstallLabor ? 'INSTALL LABOR' : 'Other Service'),
        isServiceRow: true,
        isOtherService: !isInstallLabor,
        exptQty: s.qty || '-',
        hours: displayHours,
        ot: 0,
        costRate: sCostRate,
        billRate: sBillRate,
        cost: lineCost,
        bill: sSavedBill > 0 ? sSavedBill : finalBill,
        billable: Boolean(s.billable),
        warning: false,
        originalData: s
      };
    };

    // 3. Assemble final rows
    const resultRows = [...baseLogs];
    
    // Add consolidated/fixed service rows
    if (installLaborService) {
      resultRows.push(createServiceRow(installLaborService, true));
    }
    
    otherServices.forEach(s => {
      // For T&M projects, only "Install Labor" stays in the Labor tab. 
      // All other services move to the "Other" tab.
      if (isTM) return;

      // For non-T&M projects:
      // - Services added FROM the job cost (from_job === 1) go to the "Other" tab.
      // - Services NOT added from the job cost (from_job === 0, i.e., from estimate) stay in the Labor tab.
      if (Number(s.from_job) === 1) return;

      resultRows.push(createServiceRow(s, false));
    });

    return resultRows;
  }, [rawLogs, services, isTM, projectData?.project_category, installLaborServiceRecord, refreshTrigger]);

  const taskOptions = useMemo(() => {
    // Collect unique task names from rawLogs and also from projectTasks definition
    const fromLogs = rawLogs.map(r => r.task_name || 'Install Labor');
    const fromProject = (projectTasks || []).map(t => t.name || t.task_name || 'Install Labor');
    const combined = Array.from(new Set([...fromLogs, ...fromProject]));
    const sorted = combined.sort((a, b) => alphanumericCompare(a, b));
    return ['All Tasks', ...sorted];
  }, [rawLogs, projectTasks]);

  useEffect(() => {
    if (taskOptions.length > 0 && !selectedTaskFilter) {
      setSelectedTaskFilter('All Tasks');
    }
  }, [taskOptions, selectedTaskFilter]);

  const filteredRows = useMemo(() => {
    let base = rows;
    if (selectedTaskFilter && selectedTaskFilter !== 'All Tasks') {
      base = base.filter(r => (r.task_name || 'Install Labor') === selectedTaskFilter || r.isServiceRow);
    }
    return base;
  }, [rows, selectedTaskFilter]);

  const { serviceRows, laborRows } = useMemo(() => {
    const serviceRows = filteredRows.filter(r => r.isServiceRow);
    const laborRows = filteredRows.filter(r => !r.isServiceRow);
    return { serviceRows, laborRows };
  }, [filteredRows]);

  const tmInstallLaborMemberHasHours = useMemo(() => {
    if (!isTM) return false;
    return rows.some(
      (r) => !r.isServiceRow && r.isInstallLaborRelated && rowHasLaborHours(r)
    );
  }, [rows, isTM]);

  const paginatedRows = useMemo(() => {
    const pagedLabor = laborRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    return [...serviceRows, ...pagedLabor];
  }, [serviceRows, laborRows, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (onRowsCountChange) {
      const sCount = rows.filter(r => r.isServiceRow).length;
      const lCount = rows.filter(r => !r.isServiceRow).length;
      onRowsCountChange({
        total: rows.length,
        serviceCount: sCount,
        laborCount: lCount
      });
    }
  }, [rows, onRowsCountChange]);

  const summaryStats = useMemo(() => {
    let laborCost = 0;
    let serviceCost = 0;
    let totalBillable = 0;

    rows.forEach(r => {
      // Install Labor rows are rolled into the "INSTALL LABOR" service row, not Production Labor.
      // Production task labor often has another service_id but is NOT in that rollup — it must still count toward Labor Cost.
      // Backup before non-T&M Install Labor production split:
      // const isAggregated = isTM && !r.isServiceRow && r.isInstallLaborRelated;
      const isAggregated = !r.isServiceRow && r.isInstallLaborRelated;
      if (isAggregated) return;

      const cost = Number(r.cost) || 0;
      const bill = (r.billable ? Number(r.bill) : 0) || 0;

      if (r.isServiceRow) {
        serviceCost += cost;
      } else {
        laborCost += cost;
      }
      totalBillable += bill;
    });

    const totalCost = laborCost + serviceCost;
    const marginPercent = calculateMargin(totalBillable, totalCost);

    return {
      totalCost,
      laborCost,
      serviceCost,
      totalBillable,
      marginPercent
    };
  }, [rows, isTM]);

  useEffect(() => {
    if (onSummaryChange) {
      onSummaryChange({
        cost: Number(summaryStats.totalCost || 0),
        laborCost: Number(summaryStats.laborCost || 0),
        billable: Number(summaryStats.totalBillable || 0),
        marginPercent: Number(summaryStats.marginPercent || 0)
      });
    }
  }, [summaryStats, onSummaryChange]);

  useEffect(() => {
    if (projectId) {
      fetchUsersByTask();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshTrigger, serviceRatesFingerprint, installLaborCatalogStub]);

  // OLD: no interval — labor list only refetched on projectId / refreshTrigger / serviceRatesFingerprint change
  // Keep labor hours current while members are still clocked in (open timelog on server).
  useEffect(() => {
    if (!projectId) return undefined;
    const intervalId = setInterval(() => fetchUsersByTask(true), 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshTrigger, serviceRatesFingerprint, installLaborCatalogStub]);

  useEffect(() => {
    setLaborDialogMembers([]);
    setLaborDialogTasks([]);
  }, [projectId]);

  useEffect(() => {
    if (!addDialogOpen || !projectId) return undefined;

    let cancelled = false;

    (async () => {
      setLoadingLaborDialogData(true);
      try {
        const { members, tasks } = await loadLaborAddDialogData(projectId, userId, userType);
        if (cancelled) return;
        setLaborDialogMembers(members);
        setLaborDialogTasks(tasks);
      } catch (e) {
        console.error('Error loading job context for Add Labor:', e);
        if (!cancelled) {
          setLaborDialogMembers([]);
          setLaborDialogTasks([]);
        }
      } finally {
        if (!cancelled) setLoadingLaborDialogData(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally only when dialog opens / job changes; userId & userType read at invocation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addDialogOpen, projectId]);

  const addLaborTaskOptions = laborDialogTasks.length ? laborDialogTasks : projectTasks || [];
  const hasNoTaskOptions = !loadingLaborDialogData && addLaborTaskOptions.length === 0;
  const installLaborFallbackRate = useMemo(() => {
    const fromService = installLaborServiceRecord ? resolveServiceRate(installLaborServiceRecord, isTM) : 0;
    return Number(fromService) || 0;
  }, [installLaborServiceRecord, isTM]);
  const addLaborMemberOptions = useMemo(() => {
    const base = laborDialogMembers.length ? laborDialogMembers : projectMembers || [];
    const staffIdsOnTable = new Set();
    (rows || []).forEach((r) => {
      const sid = r.originalData?.staff_id;
      if (sid != null && sid !== '') staffIdsOnTable.add(Number(sid));
    });
    return base.filter((m) => !staffIdsOnTable.has(Number(m.id ?? m.staff_id)));
  }, [laborDialogMembers, projectMembers, rows]);
  const addLaborMemberOptionsRef = useRef(addLaborMemberOptions);
  const addLaborTaskOptionsRef = useRef(addLaborTaskOptions);
  addLaborMemberOptionsRef.current = addLaborMemberOptions;
  addLaborTaskOptionsRef.current = addLaborTaskOptions;
  // OLD backup (before no-task Install Labor validation support):
  // const NewUserSchema = Yup.object().shape({
  //   memberId: Yup.mixed().required('Team Member is required'),
  //   taskId: Yup.mixed().required('Task is required'),
  //   staffName: Yup.string().trim().required('Staff Name is required'),
  //   hours: Yup.number().typeError('Hours must be a number').min(0, 'Hours cannot be negative').required('Hours is required'),
  //   ot: Yup.number().typeError('OT must be a number').min(0, 'OT cannot be negative').required('OT is required'),
  //   costRate: Yup.number().typeError('Labor Rate must be a number').min(0, 'Labor Rate cannot be negative').required('Labor Rate is required'),
  //   billRate: Yup.number().typeError('Bill Rate must be a number').min(0, 'Bill Rate cannot be negative').required('Bill Rate is required')
  // });
  const addLaborSchema = useMemo(
    () =>
      Yup.object().shape({
        memberId: Yup.mixed().required('Team Member is required'),
        // OLD: taskId was always required; in no-task Install Labor flow this caused avoidable validation errors.
        taskId: hasNoTaskOptions
          ? Yup.mixed().nullable()
          : Yup.mixed().required('Task is required'),
        staffName: Yup.string().trim().required('Staff Name is required'),
        hours: Yup.number().typeError('Hours must be a number').min(0, 'Hours cannot be negative').required('Hours is required'),
        ot: Yup.number().typeError('OT must be a number').min(0, 'OT cannot be negative').required('OT is required'),
        costRate: Yup.number().typeError('Labor Rate must be a number').min(0, 'Labor Rate cannot be negative').required('Labor Rate is required'),
        billRate: Yup.number().typeError('Bill Rate must be a number').min(0, 'Bill Rate cannot be negative').required('Bill Rate is required')
      }),
    [hasNoTaskOptions]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      employee: '',
      hours: 0,
      ot: 0,
      costRate: 0,
      billRate: 0,
      billable: true,
    },
    onSubmit: async (values) => {
      const row = rows.find(r => r.uid === editingUid);
      if (!row) return;

      if (row.isServiceRow) {
        setIsSavingService(true);
        try {
          let serviceId =
            row.serviceId ??
            row.originalData?.id ??
            row.originalData?.request_service_id;
          const isTMProject =
            Number(projectData?.project_category) === 12 ||
            projectData?.job_category_name === 'Time & Material';

          // Install Labor UI row can use catalog stub with no request_services.id yet
          if (!serviceId && Number(row.originalData?.labour_cost_service_id) === 10) {
            const catalog = installLaborServiceRecord || row.originalData;
            const catalogUnitCost = getLabourCostServiceCatalogRate(catalog, isTMProject) ||
              resolveInstallLaborServiceUnitCost(catalog, isTMProject);
            if (!resolvedRequestId || !projectId || !userId) {
              enqueueSnackbar('Missing request, project, or user for Install Labor save', { variant: 'error' });
              setIsSavingService(false);
              return;
            }
            const addRes = await axios.post(`${REST_API_END_POINT}request/add-request-service`, {
              serviceData: {
                name: catalog?.name || catalog?.service_label || 'INSTALL LABOR',
                id: 10,
                cost: Number(catalog?.cost ?? catalog?.originalCost ?? catalogUnitCost) || catalogUnitCost,
                total_cost: Number(
                  catalog?.labour_service_total_cost ??
                    catalog?.service_original_cost ??
                    catalog?.total_cost ??
                    catalogUnitCost
                ) || catalogUnitCost,
                expected_hours: 0,
                is_addition_service: 1
              },
              userId,
              requestId: resolvedRequestId,
              projectId,
              orderId: resolvedRequestId,
              projectCategory: projectData?.project_category
            });
            if (addRes.data?.status === 1) {
              const created = (addRes.data.services || []).find(
                (s) => Number(s.labour_cost_service_id) === 10
              );
              serviceId = created?.id;
              if (onServicesRefresh) onServicesRefresh();
            } else {
              enqueueSnackbar(addRes.data?.message || 'Failed to add Install Labor service', { variant: 'error' });
              setIsSavingService(false);
              return;
            }
          }

          if (!serviceId || !resolvedRequestId || !projectId || !userId) {
            enqueueSnackbar('Missing service, request, project, or user — cannot save', { variant: 'error' });
            setIsSavingService(false);
            return;
          }

          const res = await axios.post(
            `${REST_API_END_POINT}project/save-job-cost-equipment-service`,
            {
              serviceId,
              requestId: resolvedRequestId,
              projectId,
              userId,
              serviceName: values.employee,
              quantity: values.hours,
              unitCost: values.costRate,
              billRate: values.billRate,
              billable: values.billable ? 1 : 0,
              isLabor: true,
              jobcost_timestamp: Math.floor(Date.now() / 1000)
            }
          );

          if (res.data.status === 1) {
            /*
             * OLD — kept for reference if we need to restore “always clear clock-ins after service save”:
             * // After saving the service, clear existing clock-in data like Equipments tab does
             * try {
             *   await axios.post(`${REST_API_END_POINT}project/delete-staff-task-timelogs-by-service`, {
             *     serviceId: row.originalData?.id,
             *     projectId,
             *     userId,
             *     isProductionLabor: Boolean(row.originalData?.labour_cost_service_id === 10)
             *   });
             * } catch (err) {
             *   console.error('Failed to clear existing clock-in data:', err);
             * }
             */
            // After saving the service, clear related clock-in data so service and labor stay in sync.
            try {
              await axios.post(`${REST_API_END_POINT}project/delete-staff-task-timelogs-by-service`, {
                serviceId,
                projectId,
                userId,
                isProductionLabor: Boolean(row.originalData?.labour_cost_service_id === 10)
              });
            } catch (err) {
              console.error('Failed to clear existing clock-in data:', err);
            }

            enqueueSnackbar('Service updated successfully', { variant: 'success' });
            await syncLaborDataAfterMutation();
            setEditingUid(null);
          } else {
            enqueueSnackbar('Failed to update service', { variant: 'error' });
          }
        } catch (err) {
          enqueueSnackbar('Error updating service', { variant: 'error' });
        } finally {
          setIsSavingService(false);
        }
        return;
      }

      const person = row.originalData;
      const resolvedCostRate = parseLaborRateInput(values.costRate, person?.hourly_rate ?? 0);
      const resolvedBillRate = parseLaborRateInput(values.billRate, person?.bill_rate ?? 0);
      setLoadingId(editingUid);
      
      // Treat digits after the decimal point as literal minutes
      const regFloat = parseFloat(values.hours) || 0;
      const otFloat = parseFloat(values.ot) || 0;
      const otDecimal = otFloat;

      const totalH = Math.floor(regFloat + otFloat);
      const totalM = Math.round(((regFloat + otFloat) - totalH) * 60);

      const h = totalH;
      const m = totalM;

      const newTotalMinutes = Math.round((regFloat + otFloat) * 60);
      const totalHourDecimal = +(newTotalMinutes / 60).toFixed(4);

      const end_time = moment().unix();
      const start_time = end_time - newTotalMinutes * 60;
      const totalHourlyRate = Number(
        (
          (parseFloat(values.hours) || 0) * resolvedCostRate +
          (parseFloat(values.ot) || 0) * 1.5 * resolvedCostRate
        ).toFixed(2)
      );

      const payload = {
        projectId,
        taskId: person.task_id,
        userId,
        person: {
          ...person,
          staff_name: values.employee || person?.staff_name || '',
          total_hours: h,
          total_minutes: m,
          hourly_rate: resolvedCostRate,
          bill_rate: resolvedBillRate,
          billable: values.billable ? 1 : 0,
          total_hourly_rate: totalHourlyRate,
          billable_amount: values.billable
            ? Number(
                (
                  (parseFloat(values.hours) || 0) * resolvedBillRate +
                  (parseFloat(values.ot) || 0) * 1.5 * resolvedBillRate
                ).toFixed(2)
              )
            : 0,
        },
        minutesCurrent: m,
        hoursCurrent: h,
        memberClockins: [
          {
            staff_id: person.staff_id,
            userId: person.staff_user_id,
            staff_name: values.employee || person?.staff_name || '',
            hour: totalHourDecimal,
            ot_hours: otDecimal,
            hourly_rate: resolvedCostRate,
            bill_rate: resolvedBillRate,
            billable: values.billable ? 1 : 0,
            total_hourly_rate: totalHourlyRate,
            billable_amount: values.billable
              ? Number(
                  (
                    (parseFloat(values.hours) || 0) * resolvedBillRate +
                    (parseFloat(values.ot) || 0) * 1.5 * resolvedBillRate
                  ).toFixed(2)
                )
              : 0,
            subtask_id: person.subtask_id,
            task_id: person.task_id,
            start_time,
            end_time,
            service_id: person?.rs_id || person?.customer_order_product_id || person?.service_id || null,
            timelog_id: person?.timelog_id || person?.id || null,
          },
        ],
        project_category: projectData?.project_category,
        now: end_time,
        jobcost_timestamp: end_time,
        checkedItems: (person.timelog_id || person.id) ? [person.timelog_id || person.id] : [row.uid],
        request_id: resolvedRequestId
      };

      try {
        const res = await axios.post(
          `${REST_API_END_POINT}project-task/update-staff-task-timelog`,
          payload
        );

        if (res.data.status === 1) {
          enqueueSnackbar('Labor entry updated successfully', { variant: 'success' });
          await syncLaborDataAfterMutation();
          setEditingUid(null);
        } else {
          enqueueSnackbar('Failed to update labor entry', { variant: 'error' });
        }
      } catch (err) {
        console.error('❌ API Error:', err);
        enqueueSnackbar('Failed to update labor entry', { variant: 'error' });
      } finally {
        setLoadingId(null);
      }
    }
  });
  const {
    values,
    setFieldValue,
    getFieldProps
  } = formik;

  const addLaborFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      memberId: '',
      taskId: '',
      subtaskId: '',
      staffName: '',
      hours: 0,
      ot: 0,
      costRate: 0,
      billRate: 0,
      billable: true
    },
    validationSchema: addLaborSchema,
    onSubmit: async (values, { resetForm }) => {
      const memberOpts = addLaborMemberOptionsRef.current || [];
      const taskOpts = addLaborTaskOptionsRef.current || [];
      const isNoTaskInstallLaborFlow =
        values.taskId === NO_TASK_INSTALL_LABOR_VALUE || taskOpts.length === 0;
      const selectedMember = memberOpts.find(
        (m) => String(m.id) === String(values.memberId) || String(m.staff_id) === String(values.memberId)
      );
      const selectedTask = isNoTaskInstallLaborFlow ? null : taskOpts.find(
        (t) => String(t.ID || t.id) === String(values.taskId)
      );

      if (!selectedMember || (!selectedTask && !isNoTaskInstallLaborFlow)) {
        enqueueSnackbar('Please select member and task', { variant: 'error' });
        return;
      }

      const staffUserId = selectedMember.userid || selectedMember.userId || selectedMember.user_id;
      const staffId = selectedMember.staff_id || selectedMember.id;
      if (!staffUserId || !staffId) {
        enqueueSnackbar('Invalid member selection', { variant: 'error' });
        return;
      }

      const parsedHours = Number(values.hours) || 0;
      const parsedOT = Number(values.ot) || 0;
      const totalHours = parsedHours + parsedOT;
      const end_time = moment().unix();
      const start_time = end_time - Math.max(0, totalHours) * 3600;

      const totalHourlyRate = Number(((parsedHours * (Number(values.costRate) || 0)) + (parsedOT * 1.5 * (Number(values.costRate) || 0))).toFixed(2));
      const totalBillableAmount = values.billable
        ? Number(((parsedHours * (Number(values.billRate) || 0)) + (parsedOT * 1.5 * (Number(values.billRate) || 0))).toFixed(2))
        : 0;

      const payload = {
        projectId,
        taskId: isNoTaskInstallLaborFlow ? null : Number(values.taskId),
        userId,
        service_id: isNoTaskInstallLaborFlow
          ? (installLaborServiceRecord?.id || installLaborServiceRecord?.request_service_id || null)
          : (selectedTask?.rs_id || selectedTask?.customer_order_product_id || selectedTask?.service_id || null),
        request_id: resolvedRequestId || null,
        force_install_labor: isNoTaskInstallLaborFlow ? 1 : 0,
        startTime: start_time,
        endTime: end_time,
        staffId: staffUserId,
        staff: {
          id: staffId,
          user_id: staffUserId,
          name: values.staffName || selectedMember?.name || ''
        },
        staffHourlyRate: Number(values.costRate) || 0,
        staff_name: values.staffName || selectedMember?.name || '',
        subtaskId: values.subtaskId ? Number(values.subtaskId) : null,
        hours: totalHours,
        ot_hours: parsedOT,
        bill_rate: Number(values.billRate) || 0,
        billable: values.billable ? 1 : 0,
        total_hourly_rate: totalHourlyRate,
        billable_amount: totalBillableAmount,
        totalAmount: totalHourlyRate,
        jobcost_timestamp: end_time
      };

      setLoadingId('add-labor');
      try {
        const res = await axios.post(`${REST_API_END_POINT}project-task/add-manual-staff-task-timelog`, payload);
        if (res?.data?.status === 1) {
          enqueueSnackbar('Labor entry added successfully', { variant: 'success' });
          setAddDialogOpen(false);
          resetForm();
          setSubtaskOptions([]);
          await syncLaborDataAfterMutation();
        } else {
          enqueueSnackbar('Failed to add labor entry', { variant: 'error' });
        }
      } catch (error) {
        console.error('Add labor error:', error);
        enqueueSnackbar('Failed to add labor entry', { variant: 'error' });
      } finally {
        setLoadingId(null);
      }
    }
  });

  const {
    values: addLaborValues,
    errors: addLaborErrors,
    touched: addLaborTouched,
    handleSubmit: handleAddLaborSubmit,
    setFieldValue: setAddLaborFieldValue,
    resetForm: resetAddLaborForm
  } = addLaborFormik;

  useEffect(() => {
    if (!addDialogOpen) return;
    if (!hasNoTaskOptions) return;
    // OLD: taskId stayed empty when project had no task, preventing Add Labor submit.
    setAddLaborFieldValue('taskId', NO_TASK_INSTALL_LABOR_VALUE);
    setAddLaborFieldValue('subtaskId', '');
    setSubtaskOptions([]);
    setAddLaborFieldValue('costRate', installLaborFallbackRate);
  }, [addDialogOpen, hasNoTaskOptions, installLaborFallbackRate, setAddLaborFieldValue]);

  const handleTaskChangeForAdd = async (taskId) => {
    addLaborFormik.setFieldValue('taskId', taskId);
    addLaborFormik.setFieldValue('subtaskId', '');
    setSubtaskOptions([]);
    if (!taskId) return;
    try {
      const response = await axios.post(`${REST_API_END_POINT}project/fetch-all-project-sub-task`, {
        taskId: Number(taskId),
        userType,
        userId,
        isMirrored,
        targetFacilityId
      });
      if (response?.data?.status === 1) {
        setSubtaskOptions(response?.data?.result || []);
      }
    } catch (error) {
      console.error('Error fetching subtasks:', error);
    }
  };

  const handleEditRow = (row) => {
    if (row.isServiceRow) {
      setEditServiceConfirmation({ open: true, row });
      return;
    }
    setEditingUid(row.uid);
    const installLaborService = installLaborServiceRecord;
    const serviceLineCostRate = installLaborService ? resolveServiceRate(installLaborService, isTM) : 0;
    formik.setValues({
      employee: row.originalData?.staff_name || row.employee,
      hours: roundToTwo(row.hours),
      ot: row.ot,
      costRate:
        row.costRate ||
        ((row.isInstaller || row.isDriver) ? (row._actualCostRate || serviceLineCostRate || 0) : 0),
      billRate:
        row.billRate ||
        ((row.isInstaller || row.isDriver) ? (row._actualBillRate || (isTM ? (row._actualBillRate || 0) : 0)) : 0),
      billable: Boolean(row.billable)
    });
  };

  const handleEditServiceConfirm = () => {
    const row = editServiceConfirmation.row;
    if (!row) return;
    setEditingUid(row.uid);

    const actualCostRate = resolveServiceRateForEdit(row.originalData, isTM);
    const actualBillRate = resolveServiceBillRate(row.originalData);

    formik.setValues({
      employee: row.employee,
      hours: roundToTwo(row.hours),
      ot: 0,
      costRate: actualCostRate > 0 ? actualCostRate : row.costRate,
      billRate: actualBillRate > 0 ? actualBillRate : row.billRate,
      billable: Boolean(row.billable)
    });
    setEditServiceConfirmation({ open: false, row: null });
  };

  const handleCancelEdit = () => {
    setEditingUid(null);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    if (itemToDelete.isServiceRow) {
      setLoadingId(itemToDelete.uid);
      try {
        const deleteServiceId =
          itemToDelete.serviceId ?? itemToDelete.originalData?.id;
        const from_job = Number(projectData?.project_category) === 12 ? 1 : (itemToDelete.originalData?.from_job || 0);
        const res = await axios.post(`${REST_API_END_POINT}request/delete-request-service`, {
          serviceId: deleteServiceId,
          userId,
          from_job,
          is_recipe_group: Boolean(itemToDelete.originalData?.__isRecipeGroup),
          request_id: resolvedRequestId,
          projectId,
          orderId: resolvedRequestId
        });
        if (res.data.status === 1) {
          enqueueSnackbar('Service removed', { variant: 'success' });
          await syncLaborDataAfterMutation();
        } else {
          enqueueSnackbar('Failed to remove service', { variant: 'error' });
        }
      } catch (err) {
        enqueueSnackbar('Error removing service', { variant: 'error' });
      } finally {
        setLoadingId(null);
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      }
      return;
    }

    setLoadingId(itemToDelete.uid);
    try {
      const person = itemToDelete.originalData;
      // const previousTotalHours = person.total_hours || 0;
      // const previousTotalMinutes = person.total_minutes || 0;

      // const newTotalMinutes = 0;
      // OLD: negative delta made start_time > end_time on delete
      // const deltaMinutes = newTotalMinutes - (previousTotalHours * 60 + previousTotalMinutes);
      // const hourDecimal = +(deltaMinutes / 60).toFixed(4);
      // const end_time = moment().unix();
      // const start_time = end_time - deltaMinutes * 60;
      const hourDecimal = 0;
      const end_time = moment().unix();
      const start_time = end_time;

      const payload = {
        projectId,
        taskId: person.task_id,
        isDelete: true,
        userId,
        person: {
          ...person,
          total_hours: 0,
          total_minutes: 0,
          hourly_rate: person?.hourly_rate || 0,
          bill_rate: person?.bill_rate || 0,
          billable: itemToDelete.billable ? 1 : 0,
          total_hourly_rate: 0,
          billable_amount: 0,
        },
        minutesCurrent: 0,
        hoursCurrent: 0,
        memberClockins: [
          {
            staff_id: person.staff_id,
            userId: person.staff_user_id,
            hour: hourDecimal,
            subtask_id: person.subtask_id,
            task_id: person.task_id,
            start_time,
            end_time,
            service_id: person?.rs_id || person?.customer_order_product_id || person?.service_id || null,
            hourly_rate: person?.hourly_rate || 0,
            bill_rate: person?.bill_rate || 0,
            billable: itemToDelete.billable ? 1 : 0,
            total_hourly_rate: 0,
            timelog_id: person?.timelog_id || null,
          },
        ],
        project_category: projectData?.project_category,
        now: end_time,
        jobcost_timestamp: end_time,
        checkedItems: person.timelog_id ? [person.timelog_id] : [itemToDelete.uid],
        request_id: resolvedRequestId
      };

      const res = await axios.post(
        `${REST_API_END_POINT}project-task/update-staff-task-timelog`,
        payload
      );

      if (res.data.status === 1) {
        enqueueSnackbar('Labor entry removed', { variant: 'success' });
        await syncLaborDataAfterMutation();
      } else {
        enqueueSnackbar('Failed to remove labor entry', { variant: 'error' });
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Error removing labor entry', { variant: 'error' });
    } finally {
      setLoadingId(null);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Autocomplete
          size="small"
          options={taskOptions}
          value={selectedTaskFilter}
          onChange={(event, newValue) => {
            setSelectedTaskFilter(newValue);
            setPage(0);
          }}
          sx={{ width: 220 }}
          renderInput={(params) => <TextField {...params} label="Task" size="small" />}
        />
        <Tooltip title={`Labor Cost = Sum of individual labor logs = $${summaryStats.laborCost.toFixed(2)}`} arrow>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'transparent', px: 2, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box component={'span'} className='mdi--dollar' width={18} height={18} sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Labor Cost: ${summaryStats.laborCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title={`Service Cost = Sum of all service rows = $${summaryStats.serviceCost.toFixed(2)}`} arrow>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'primary.main', px: 2, py: 1, borderRadius: 1 }}>
            <Box component={'span'} className='mdi--dollar' width={18} height={18} sx={{ mr: 0.5, color: 'common.white' }} />
            <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 800 }}>
              Service Cost: ${summaryStats.serviceCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>
        </Tooltip>
        {/* {!isTM && (
          <Tooltip title={`Total Billable = Sum of Bill where Billable is checked = $${summaryStats.totalBillable.toFixed(2)}`} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'primary.main', px: 2, py: 1, borderRadius: 1 }}>
              <Box component={'span'} className='mdi--dollar' width={18} height={18} sx={{ mr: 0.5, color: 'common.white' }} />
              <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 800 }}>
                Billable: ${summaryStats.totalBillable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Tooltip>
        )} */}
        {/* {!isTM && (
          <Tooltip
            title={`Margin % = ((Billable - Cost) / Cost) x 100 = (($${summaryStats.totalBillable.toFixed(2)} - $${summaryStats.totalCost.toFixed(2)}) / $${summaryStats.totalCost.toFixed(2)}) x 100 = ${summaryStats.marginPercent.toFixed(2)}%`}
            arrow
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: (theme) =>
                  summaryStats.marginPercent < 0
                    ? (theme.palette.mode === 'dark' ? `${theme.palette.error.main}1a` : '#ffebee')
                    : (theme.palette.mode === 'dark' ? `${theme.palette.success.main}1a` : '#e8f5e9'),
                px: 2,
                py: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: summaryStats.marginPercent < 0 ? 'error.light' : 'success.light'
              }}
            >
              <Box
                component={'span'}
                className={summaryStats.marginPercent < 0 ? 'ph--trend-down' : 'ph--trend-up'}
                width={18}
                height={18}
                sx={{ mr: 1, color: summaryStats.marginPercent < 0 ? 'error.main' : '#54D62C' }}
              />
              <Typography variant="subtitle2" sx={{ color: summaryStats.marginPercent < 0 ? 'error.dark' : 'success.dark', fontWeight: 800 }}>
                Margin: {summaryStats.marginPercent.toFixed(2)}%
              </Typography>
            </Box>
          </Tooltip>
        )} */}
      </Stack>
      <Card sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, overflow: 'hidden' }}>
        <Scrollbar>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {getTableHead(isTM).map((head) => (
                    <TableCell
                      key={head.id}
                      align={head.align}
                      sx={{ fontWeight: 800, color: 'text.secondary', py: 1.8, fontSize: '0.8rem', textTransform: 'uppercase' }}
                    >
                      {head.label}
                    </TableCell>
                  ))}
                </TableRow>

                <AnimatePresence>
                  {loading ? (
                    [...Array(4)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(getTableHead(isTM).length)].map((_, j) => (
                          <TableCell key={j}><Skeleton variant="text" width="60%" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={getTableHead(isTM).length} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">No labor entries found for this task</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row, index) => {
                      const isEditing = editingUid === row.uid;
                      const isRowLoading = loadingId === row.uid;
                      const hasLaborHours = (Number(row.hours) || 0) + (Number(row.ot) || 0) > 0;
                      const displayBillRate = hasLaborHours ? row.billRate : 0;
                      const displayBillAmount = hasLaborHours ? row.bill : 0;
                      const displayBillable = hasLaborHours ? row.billable : false;

                      return (
                        <TableRow
                          key={row.uid}
                          component={motion.tr}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          sx={{
                            ...(row.warning && {
                              bgcolor: (theme) => `${theme.palette.warning.main} !important`,
                              '& .MuiTableCell-root': { color: 'text.primary', borderBottom: 'none' }
                            }),
                            ...(row.isServiceRow && {
                              bgcolor: 'primary.main',
                              '& .MuiTableCell-root': { color: 'common.white', borderBottom: 'none' },
                              '& .MuiTypography-root': { color: 'common.white', fontWeight: 800 },
                              '& .MuiIconButton-root': { color: 'common.white' },
                              '& .mdi--dollar, .ph--trend-up, .ph--trend-down': { color: 'common.white !important' }
                            }),
                            '&:hover': { bgcolor: row.warning ? 'warning.light' : row.isServiceRow ? 'primary.dark' : 'action.hover' }
                          }}
                        >
                          {!isTM && (<TableCell>{row.date}</TableCell>)}

                          {isEditing ? (
                            <FormikProvider value={formik}>
                              <TableCell>
                                {isTM || row.isServiceRow ? (
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    {/* {row.isServiceRow &&
                                      row.originalData?.finalTaken === 1 && 
                                      projectData?.syncWithQuickbooks === 1 && row.hours > 0 && (
                                        <Avatar
                                          src={QbLogo}
                                          alt="QB"
                                          sx={{ width: 24, height: 24, objectFit: 'cover' }}
                                        />
                                      )} */}
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {values.employee}
                                    </Typography>
                                  </Stack>
                                ) : (
                                  <TextField
                                    size="small"
                                    variant="outlined"
                                    {...getFieldProps('employee')}
                                    onChange={(e) => setFieldValue('employee', e.target.value)}
                                    sx={{ minWidth: 150 }}
                                  />
                                )}
                              </TableCell>
                              {!isTM && (
                                <TableCell align="center">
                                  {row.isServiceRow ? (
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                      {row.exptQty}
                                    </Typography>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                              )}
                              <TableCell align="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  {...getFieldProps('hours')}
                                  onChange={(e) => setFieldValue('hours', e.target.value)}
                                  onKeyDown={preventInvalidNumberKeys}
                                  inputProps={{ min: 0, step: 1 }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              {/* {!isTM && (
                                <TableCell align="center">
                                  <TextField
                                    size="small"
                                    type="number"
                                    {...getFieldProps('ot')}
                                    onChange={(e) => setFieldValue('ot', e.target.value)}
                                    onKeyDown={preventInvalidNumberKeys}
                                    inputProps={{ min: 0, step: 'any' }}
                                    sx={{ width: 60 }}
                                  />
                                </TableCell>
                              )} */}
                              <TableCell>
                                {isTM || row.isServiceRow ? (
                                  <Typography variant="body2">
                                    ${Number(values.costRate || 0).toFixed(2)}
                                  </Typography>
                                ) : (
                                  <TextField
                                    size="small"
                                    type="number"
                                    {...getFieldProps('costRate')}
                                    onChange={(e) => setFieldValue('costRate', e.target.value)}
                                    onKeyDown={preventInvalidNumberKeys}
                                    inputProps={{ min: 0, step: 'any' }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    sx={{ width: 100 }}
                                  />
                                )}
                              </TableCell>
                              {/* {!isTM && (
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="number"
                                    {...getFieldProps('billRate')}
                                    onChange={(e) => setFieldValue('billRate', e.target.value)}
                                    onKeyDown={preventInvalidNumberKeys}
                                    inputProps={{ min: 0, step: 'any' }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    sx={{ width: 100 }}
                                  />
                                </TableCell>
                              )} */}
                              <TableCell sx={{ minWidth: 100 }}>
                                <Tooltip
                                  title={isTM || row.isServiceRow
                                    ? `Quantity: ${(parseFloat(values.hours) || 0).toFixed(2)} x $${(parseFloat(values.costRate) || 0).toFixed(2)} = $${((parseFloat(values.hours) || 0) * (parseFloat(values.costRate) || 0)).toFixed(2)}`
                                    : getAmountBreakdown(
                                      parseFloat(values.hours) || 0,
                                      parseFloat(values.ot) || 0,
                                      parseFloat(values.costRate) || 0
                                    ).text}
                                  arrow
                                >
                                  <span>
                                    ${getAmountBreakdown(
                                      parseFloat(values.hours) || 0,
                                      parseFloat(values.ot) || 0,
                                      parseFloat(values.costRate) || 0
                                    ).total.toFixed(2)}
                                  </span>
                                </Tooltip>
                              </TableCell>
                              {/* {!isTM && (
                                <>
                                  <TableCell sx={{ minWidth: 100, fontWeight: 900, color: 'primary.main' }}>
                                    <Tooltip
                                      title={getAmountBreakdown(
                                        parseFloat(values.hours) || 0,
                                        parseFloat(values.ot) || 0,
                                        parseFloat(values.billRate) || 0
                                      ).text}
                                      arrow
                                    >
                                      <span>
                                        ${getAmountBreakdown(
                                          parseFloat(values.hours) || 0,
                                          parseFloat(values.ot) || 0,
                                          parseFloat(values.billRate) || 0
                                        ).total.toFixed(2)}
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Checkbox
                                      checked={Boolean(values.billable)}
                                      onChange={(e) => setFieldValue('billable', e.target.checked)}
                                    />
                                  </TableCell>
                                </>
                              )} */}
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  {isRowLoading ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <>
                                      <IconButton size="small" color="primary" onClick={formik.handleSubmit}>
                                        <Icon icon={saveFill} width={18} height={18} />
                                      </IconButton>
                                      <IconButton size="small" color="inherit" onClick={handleCancelEdit}>
                                        <Icon icon={closeFill} width={18} height={18} />
                                      </IconButton>
                                    </>
                                  )}
                                </Stack>
                              </TableCell>
                            </FormikProvider>
                          ) : (
                            <>
                              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  {(((row.isServiceRow &&
                                    projectData?.syncWithQuickbooks === 1 && row.cost  > 0)) || (row?.syncedWithQuickbooks === 1 && row.hours > 0)) && (
                                      <Avatar
                                        src={QbLogo}
                                        alt="QB"
                                        sx={{ width: 24, height: 24, objectFit: 'cover' }}
                                      />
                                    )}
                                  <Tooltip
                                    title={row.employeeTooltip || row.employee || ''}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          maxWidth: 320,
                                          whiteSpace: 'pre-line',
                                          wordBreak: 'break-word'
                                        }
                                      }
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, maxWidth: 220 }}
                                      noWrap
                                    >
                                      {row.employee}
                                    </Typography>
                                  </Tooltip>
                                  {row.warning && (
                                    <Tooltip title="Rate overridden">
                                      <Box component="span" sx={{ ml: 1, display: 'inline-flex', verticalAlign: 'middle' }}>
                                        <Icon icon={alertTriangleFill} width={16} height={16} style={{ color: theme.palette.warning.dark }} />
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                              {!isTM && (
                                <TableCell align="center">
                                  <Typography variant="body2" sx={{ fontWeight: row.isServiceRow ? 800 : 400 }}>
                                    {row.exptQty}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell align="center">
                                <Tooltip title={isTM || row.isServiceRow ? `${Number(row.hours || 0).toFixed(2)} Hours` : getAmountBreakdown(row.hours, row.ot, 0).hoursText} arrow>
                                  <Typography variant="body2" sx={{ fontWeight: row.isServiceRow ? 800 : 400 }}>
                                    {Number(row.hours || 0).toFixed(2)}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>${row.costRate.toFixed(2)}</TableCell>
                              <TableCell>
                                <Tooltip
                                  title={isTM || row.isServiceRow
                                    ? `Quantity: ${Number(row.hours || 0).toFixed(2)} x $${Number(row.costRate || 0).toFixed(2)} = $${Number(row.cost || 0).toFixed(2)}`
                                    : getAmountBreakdown(row.hours, row.ot, row.costRate).text}
                                  arrow
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    ${Number(row.cost || 0).toFixed(2)}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              {/* {!isTM && (
                                <TableCell sx={{ fontWeight: 900, color: 'primary.main' }}>
                                  <Tooltip title={getAmountBreakdown(row.hours, row.ot, displayBillRate).text} arrow>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: 'primary.main' }}>${displayBillAmount.toFixed(2)}</Typography>
                                  </Tooltip>
                                </TableCell>
                              )}
                              {!isTM && (
                                <TableCell align="center">
                                  <Box sx={{
                                    width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: displayBillable ? 'success.main' : 'error.main', mx: 'auto'
                                  }}>
                                    <Icon icon={displayBillable ? checkmarkCircle2Fill : closeCircleFill} width={14} height={14} style={{ color: 'white' }} />
                                  </Box>
                                </TableCell>
                              )} */}
                              {/* OLD: T&M hid entire Actions column; non-T&M only
                              { isTM ? "":
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <IconButton size="small" onClick={() => handleEditRow(row)} disabled={isMirrored}>
                                    <Icon icon={edit2Fill} width={18} height={18} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setItemToDelete(row);
                                      setDeleteDialogOpen(true);
                                    }}
                                    disabled={isMirrored}
                                  >
                                    <Icon icon={trash2Fill} width={18} height={18} />
                                  </IconButton>
                                </Stack>
                              </TableCell>}
                              */}
                              {/* OLD: T&M Install Labor service row always "-"; member rows Edit+Delete
                              <TableCell align="center">
                                {isTM && !showTmLaborRowActions(row) ? (
                                  '-'
                                ) : (
                                  <Stack direction="row" spacing={1} justifyContent="center">
                                    <IconButton size="small" onClick={() => handleEditRow(row)} disabled={isMirrored}>
                                      <Icon icon={edit2Fill} width={18} height={18} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        setItemToDelete(row);
                                        setDeleteDialogOpen(true);
                                      }}
                                      disabled={isMirrored}
                                    >
                                      <Icon icon={trash2Fill} width={18} height={18} />
                                    </IconButton>
                                  </Stack>
                                )}
                              </TableCell>
                              */}
                              <TableCell align="center">
                                {isTM && isTmInstallLaborServiceRow(row) ? (
                                  tmInstallLaborMemberHasHours ? (
                                    '-'
                                  ) : (
                                    <IconButton size="small" onClick={() => handleEditRow(row)} disabled={isMirrored}>
                                      <Icon icon={edit2Fill} width={18} height={18} />
                                    </IconButton>
                                  )
                                ) : (
                                  <Stack direction="row" spacing={1} justifyContent="center">
                                    <IconButton size="small" onClick={() => handleEditRow(row)} disabled={isMirrored}>
                                      <Icon icon={edit2Fill} width={18} height={18} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        setItemToDelete(row);
                                        setDeleteDialogOpen(true);
                                      }}
                                      disabled={isMirrored}
                                    >
                                      <Icon icon={trash2Fill} width={18} height={18} />
                                    </IconButton>
                                  </Stack>
                                )}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
        <TablePagination
          rowsPerPageOptions={[15, 25, 50]}
          component="div"
          count={laborRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
      <Button
        variant="text"
        onClick={() => {
          resetAddLaborForm();
          setSubtaskOptions([]);
          setAddDialogOpen(true);
        }}
        disabled={isMirrored}
        startIcon={<Box component="span" sx={{ fontSize: 20, lineHeight: 1, fontWeight: 500 }}>+</Box>}
        sx={{ mt: 2, pl: 1, color: 'primary.main', fontWeight: 700 }}
      >
        Add Labor Entry
      </Button>

      <ConfirmationPopup
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        message={`Are you sure you want to remove this ${itemToDelete?.isServiceRow ? 'service' : 'labor entry'}?`}
        handleClick={handleDelete}
      />

      <Dialog open={editServiceConfirmation.open} onClose={() => setEditServiceConfirmation({ open: false, row: null })}>
        <DialogContent>
          <Alert severity="warning">
            When we edit the hours, any already clocked-in data (if present) will be cleared and replaced with the service hourly cost. If no clock-in data exists, only the service hourly cost will be applied. Are you sure?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setEditServiceConfirmation({ open: false, row: null })}>
            No
          </Button>
          <Button variant="contained" color="primary" onClick={handleEditServiceConfirm}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={addDialogOpen} handleClose={() => setAddDialogOpen(false)} modalTitle="Add Labor Entry" width={700}>
        <FormikProvider value={addLaborFormik}>
          <Form noValidate autoComplete="off" onSubmit={handleAddLaborSubmit}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Autocomplete
                options={addLaborMemberOptions}
                loading={loadingLaborDialogData}
                getOptionLabel={(option) => option?.name || ''}
                value={
                  addLaborMemberOptions.find(
                    (m) => String(m.id || m.staff_id) === String(addLaborValues.memberId)
                  ) || null
                }
                onChange={(e, selected) => {
                  setAddLaborFieldValue('memberId', selected ? (selected.id || selected.staff_id) : '');
                  setAddLaborFieldValue('staffName', selected?.name || '');
                  // OLD: default labor rate from team member hourly only.
                  // setAddLaborFieldValue('costRate', Number(selected?.hourly_rate) || 0);
                  const installLaborService = installLaborServiceRecord;
                  const fromService = installLaborService ? resolveServiceRate(installLaborService, isTM) : 0;
                  const fromMember = Number(selected?.hourly_rate) || 0;
                  setAddLaborFieldValue('costRate', fromService > 0 ? fromService : fromMember);
                  setAddLaborFieldValue('billRate', Number(selected?.bill_rate) || 0);
                }}
                disabled={loadingLaborDialogData}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Team Member"
                    fullWidth
                    error={Boolean(addLaborTouched.memberId && addLaborErrors.memberId)}
                    helperText={addLaborTouched.memberId && addLaborErrors.memberId}
                  />
                )}
              />

              {/* OLD backup: Staff Name field removed from Add Labor Entry UI — name comes from Team Member selection.
              <TextField
                label="Staff Name"
                value={addLaborValues.staffName}
                onChange={(e) => setAddLaborFieldValue('staffName', e.target.value)}
                error={Boolean(addLaborTouched.staffName && addLaborErrors.staffName)}
                helperText={addLaborTouched.staffName && addLaborErrors.staffName}
                fullWidth
              />
              */}

              {hasNoTaskOptions ? (
                <TextField
                  label="Task"
                  value="Install Labor"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              ) : (
                <Autocomplete
                  options={addLaborTaskOptions}
                  loading={loadingLaborDialogData}
                  getOptionLabel={(option) => option?.name || ''}
                  value={
                    addLaborTaskOptions.find(
                      (t) => String(t.ID || t.id) === String(addLaborValues.taskId)
                    ) || null
                  }
                  onChange={(e, selected) => handleTaskChangeForAdd(selected ? (selected.ID || selected.id) : '')}
                  disabled={loadingLaborDialogData}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Task"
                      fullWidth
                      error={Boolean(addLaborTouched.taskId && addLaborErrors.taskId)}
                      helperText={addLaborTouched.taskId && addLaborErrors.taskId}
                    />
                  )}
                />
              )}

              {!hasNoTaskOptions && (
                // OLD backup (before no-task Install Labor UX): Subtask always rendered and only disabled.
                // <Autocomplete
                //   options={subtaskOptions || []}
                //   getOptionLabel={(option) => option?.name || option?.sub_task_name || ''}
                //   value={
                //     (subtaskOptions || []).find(
                //       (s) => String(s.ID || s.id) === String(addLaborValues.subtaskId)
                //     ) || null
                //   }
                //   onChange={(e, selected) => setAddLaborFieldValue('subtaskId', selected ? (selected.ID || selected.id) : '')}
                //   disabled={loadingLaborDialogData || !addLaborValues.taskId || hasNoTaskOptions}
                //   renderInput={(params) => (
                //     <TextField
                //       {...params}
                //       label="Subtask"
                //       fullWidth
                //     />
                //   )}
                // />
                <Autocomplete
                  options={subtaskOptions || []}
                  getOptionLabel={(option) => option?.name || option?.sub_task_name || ''}
                  value={
                    (subtaskOptions || []).find(
                      (s) => String(s.ID || s.id) === String(addLaborValues.subtaskId)
                    ) || null
                  }
                  onChange={(e, selected) => setAddLaborFieldValue('subtaskId', selected ? (selected.ID || selected.id) : '')}
                  disabled={loadingLaborDialogData || !addLaborValues.taskId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subtask"
                      fullWidth
                    />
                  )}
                />
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Hours"
                  type="number"
                  value={addLaborValues.hours}
                  onChange={(e) => setAddLaborFieldValue('hours', e.target.value)}
                  onKeyDown={preventInvalidNumberKeys}
                  inputProps={{ min: 0, step: 1 }}
                  error={Boolean(addLaborTouched.hours && addLaborErrors.hours)}
                  helperText={addLaborTouched.hours && addLaborErrors.hours}
                  fullWidth
                />
                {/* {!isTM && (
                  <TextField
                    label="OT"
                    type="number"
                    value={addLaborValues.ot}
                    onChange={(e) => setAddLaborFieldValue('ot', e.target.value)}
                    onKeyDown={preventInvalidNumberKeys}
                    inputProps={{ min: 0, step: 1 }}
                    error={Boolean(addLaborTouched.ot && addLaborErrors.ot)}
                    helperText={addLaborTouched.ot && addLaborErrors.ot}
                    fullWidth
                  />
                )} */}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Labor Rate"
                  type="number"
                  value={addLaborValues.costRate}
                  onChange={(e) => {
                    if (hasNoTaskOptions) return;
                    setAddLaborFieldValue('costRate', e.target.value);
                  }}
                  onKeyDown={preventInvalidNumberKeys}
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    readOnly: true
                  }}
                  error={Boolean(addLaborTouched.costRate && addLaborErrors.costRate)}
                  helperText={addLaborTouched.costRate && addLaborErrors.costRate}
                  fullWidth
                />
                {/* {!isTM && (
                  <TextField
                    label="Bill Rate"
                    type="number"
                    value={addLaborValues.billRate}
                    onChange={(e) => setAddLaborFieldValue('billRate', e.target.value)}
                    onKeyDown={preventInvalidNumberKeys}
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    error={Boolean(addLaborTouched.billRate && addLaborErrors.billRate)}
                    helperText={addLaborTouched.billRate && addLaborErrors.billRate}
                    fullWidth
                  />
                )} */}
              </Stack>

              {/* {!isTM && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={Boolean(addLaborValues.billable)}
                    onChange={(e) => setAddLaborFieldValue('billable', e.target.checked)}
                  />
                  <Typography variant="body2">Billable</Typography>
                </Stack>
              )} */}

              <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
                <Button type="submit" variant="contained" disabled={loadingId === 'add-labor' || loadingLaborDialogData}>
                  {loadingId === 'add-labor' ? 'Saving...' : 'Add Labor Entry'}
                </Button>
                <Button onClick={() => setAddDialogOpen(false)} color="inherit">
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </Modal>
    </Box>
  );
}

// PropTypes matching what's passed from recipeCost.js
JobCostMemberClockInTimer.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectData: PropTypes.object,
  fetchData: PropTypes.func,
  fetchJobCostLogs: PropTypes.func,
  isMirrored: PropTypes.bool,
  targetFacilityId: PropTypes.number,
  projectMembers: PropTypes.array,
  projectTasks: PropTypes.array,
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRowsCountChange: PropTypes.func,
  refreshTrigger: PropTypes.number,
  onRefreshTrigger: PropTypes.func,
  onSummaryChange: PropTypes.func,
  services: PropTypes.array,
  isTM: PropTypes.bool,
  onServicesRefresh: PropTypes.func
};